import axios from "axios";
import { COIN_SYMBOL_IDS } from "../../../SymbolData";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import StorageManager from "../../../utils/StorageManager";
// DISABLED: Fake spin logic replaced with instant freeze on errors
// import { generateNonWinningCombination } from "../../../utilities";

/* -------------------------------------------------------
   HELPER: Normalize Screen Data

   Converts string symbol IDs to numbers (no transformation)
------------------------------------------------------- */
function normalizeScreenData(screenData) {
  if (!screenData || !Array.isArray(screenData)) {
    return screenData;
  }

  return screenData.map((reel) => {
    if (!Array.isArray(reel)) return reel;
    return reel.map((symbolId) => {
      return typeof symbolId === "string" ? parseInt(symbolId, 10) : symbolId;
    });
  });
}

/* -------------------------------------------------------
   HELPER: Check if screen has regular symbols (non-coin, non-blank)

   Returns true if screen contains any regular symbols (4, 5, 6, 7, 8, 9, 10)
   This indicates reload happened right after trigger (before first respin)
------------------------------------------------------- */
function hasRegularSymbols(screenData) {
  if (!screenData || !Array.isArray(screenData)) {
    return false;
  }

  for (const reel of screenData) {
    if (!Array.isArray(reel)) continue;
    for (const symbolId of reel) {
      const normalizedSymbolId =
        typeof symbolId === "string" ? parseInt(symbolId, 10) : symbolId;
      // Check if it's not a coin and not a blank (0)
      if (
        normalizedSymbolId !== 0 &&
        !COIN_SYMBOL_IDS.includes(normalizedSymbolId)
      ) {
        return true; // Found a regular symbol
      }
    }
  }
  return false; // Only coins and blanks
}

/* -------------------------------------------------------
   HELPER: Transform screen for Hold and Win rendering

   Replaces all non-coin symbols with 0 (blank)
   Used for trigger rounds and intro reloads where backend sends coins + regular symbols
------------------------------------------------------- */
function transformForHoldAndWin(normalizedReels) {
  return normalizedReels.map((reel) => {
    if (!Array.isArray(reel)) return reel;
    return reel.map((symbolId) => {
      const normalizedSymbolId =
        typeof symbolId === "string" ? parseInt(symbolId, 10) : symbolId;
      return COIN_SYMBOL_IDS.includes(normalizedSymbolId)
        ? normalizedSymbolId
        : 0;
    });
  });
}

/* -------------------------------------------------------
   WORKER FACTORY AS BLOB

   Helper function to create inline Web Worker from code string.
   Uses Blob URL pattern consistent with other RGS providers.
------------------------------------------------------- */
function createInlineWorker(code) {
  const blob = new Blob([code], { type: "application/javascript" });
  const url = URL.createObjectURL(blob);
  return new Worker(url);
}

/* -------------------------------------------------------
   INLINE spinWorker.js

   Worker Lifecycle Notes:
   - Worker instance will be created in spin() method
   - Worker can be reused for multiple spins
   - Worker termination handled by browser when page unloads
   - Memory management: single worker instance per provider instance
------------------------------------------------------- */
const spinWorkerCode = `
  importScripts("https://cdn.jsdelivr.net/npm/axios/dist/axios.min.js");

  onmessage = async (event) => {
    const { bet, token, apiUrl, csrfTokenName, csrfTokenValue, identifier, command = "spin", purchasedFeature, options } = event.data;

    // Validate required fields
    if (!bet || !token || !apiUrl || !csrfTokenName || !csrfTokenValue) {
      postMessage({ error: "PopiPlay: Spin API call failed: Missing required parameters" });
      return;
    }

    try {
      // CSRF header handling in worker: PopiPlay requires CSRF tokens for all API calls
      // CSRF header name and value are provided dynamically via window.__OPTIONS__
      // Header name is dynamic (e.g., "X-CSRF-Token") and value is a dynamic token
      // Both are included in the request headers using computed property name syntax
      // API URL structure: apiUrl includes play_token in path (same URL used for all spins)
      // Command parameter: "spin" for regular game, "respin" for feature game (determined by available_actions)
      const response = await axios.post(
        apiUrl, // API URL includes play_token in path (from window.__OPTIONS__.api)
        {
          command: command, // Dynamic command: "spin" or "respin" based on game state
          options: options || {
            bet: bet,
            ...(purchasedFeature ? { purchased_feature: purchasedFeature } : {})
          }
        },
        {
          headers: {
            [csrfTokenName]: csrfTokenValue, // Dynamic CSRF header: header name and value from window.__OPTIONS__
            "Content-Type": "application/json"
          }
        }
      );

      // Validate response structure
      if (!response.data) {
        postMessage({ error: "PopiPlay: Spin API call failed: Invalid response structure" });
        return;
      }

      // Check if response contains errors (API returns 200 OK with errors in body)
      if (response.data.errors && Array.isArray(response.data.errors) && response.data.errors.length > 0) {
        // Send error response
        postMessage({ error: response.data });
        return;
      }

      // Send raw API response - balance will be extracted in provider spin() method
      // Note: balance comes as {game: 0, wallet: 79461, free_rounds: 0}, we extract wallet later
      postMessage(response.data);

    } catch (error) {
      // Send full error response if available, otherwise send error message
      if (error.response?.data?.errors) {
        // Send full error object with errors array
        postMessage({ error: error.response.data });
      } else {
        // Fallback to error message
        const errorMessage = "PopiPlay: Spin API call failed: " + error.message;
        postMessage({ error: errorMessage });
      }
    }
  };
`;

/**
 * RGS provider implementation
 *
 * Provides provider-specific implementation for game initialization,
 * spin execution, and simulation functionality.
 *
 * The class follows the established provider pattern structure,
 * with method signatures compatible with existing game code that expects token parameters.
 */
class PopiPlayRGS {
  /**
   * Constructor
   *
   * Initializes the API base URL from window.__OPTIONS__ if available,
   * otherwise falls back to an empty string for development/testing.
   *
   * window.__OPTIONS__ is server-embedded configuration provided by the server.
   * It contains: api, play_token, csrfTokenHeaderName, csrfTokenHeaderValue, identifier, currency
   */
  constructor() {
    // Extract API URL from window.__OPTIONS__ (server-embedded configuration)
    // API URL includes play_token in path
    this.api_base_url = window.__OPTIONS__?.api || "";
    this.worker = null; // Lazy initialization in spin() method - worker created on first spin() call
    this.currentToken = null; // Token from previous spin (flow.round_id) - rotates with each spin
  }

  /**
   * Transform response by normalizing symbol IDs
   *
   * Screen contains visible symbols (typically 4 per reel).
   * The controller handles all sprite management programmatically,
   * so we just need to normalize string IDs to numbers.
   *
   * @param {Object} response - API response
   * @returns {Object} Response with normalized reels
   */
  transformReelsSymbols(response) {
    if (response.outcome && Array.isArray(response.outcome.screen)) {
      // Just normalize screen symbols from strings to numbers (no buffers needed)
      response.outcome.reels = response.outcome.screen.map((reel) => {
        if (!Array.isArray(reel)) {
          return [];
        }

        // Normalize symbols from strings to numbers
        return reel.map((symbolValue) => {
          if (typeof symbolValue === "string") {
            return parseInt(symbolValue, 10);
          }
          return symbolValue;
        });
      });

      // Also normalize the screen array for consistency
      response.outcome.screen = response.outcome.screen.map((reel) => {
        if (!Array.isArray(reel)) {
          return reel;
        }
        return reel.map((symbolValue) => {
          if (typeof symbolValue === "string") {
            return parseInt(symbolValue, 10);
          }
          return symbolValue;
        });
      });
    }
    return response;
  }

  /**
   * Get provider-specific response handler configuration
   *
   * @returns {Object} Handler configuration with patterns and propertyMap
   */
  getResponseHandler() {
    return {
      patterns: {
        freeSpins: [
          {
            condition: (response) => false,
            extractFreeSpins: (round) => {
              return [];
            },
            extractTriggerRound: (round) => {
              return round;
            },
          },
        ],
        regular: {
          condition: (response) =>
            response.flow?.state === "ready" ||
            response.flow?.state === "closed" ||
            response.flow?.state === "freespins",
          extractRound: (outcome) => {
            if (!outcome) {
              return {};
            }

            const transformedOutcome = { ...outcome };

            if (Array.isArray(outcome.wins)) {
              transformedOutcome.wins = outcome.wins
                .map((winTuple) => {
                  if (!Array.isArray(winTuple) || winTuple.length < 4) {
                    return null;
                  }

                  const [, amount, positions, lineId] = winTuple;

                  if (!Array.isArray(positions)) {
                    return null;
                  }

                  const transformedPositions = positions.map(
                    (rowValue, reelIndex) => {
                      if (Array.isArray(rowValue) && rowValue.length >= 2) {
                        return [rowValue[0], rowValue[1]];
                      }
                      return [reelIndex, rowValue];
                    },
                  );

                  let symbol = null;
                  if (
                    Array.isArray(outcome.screen) &&
                    transformedPositions.length > 0
                  ) {
                    const firstPosition = transformedPositions[0];
                    if (
                      Array.isArray(firstPosition) &&
                      firstPosition.length >= 2
                    ) {
                      const [reelIdx, rowIdx] = firstPosition;
                      if (
                        outcome.screen[reelIdx] &&
                        Array.isArray(outcome.screen[reelIdx]) &&
                        outcome.screen[reelIdx][rowIdx] !== undefined
                      ) {
                        const symbolValue = outcome.screen[reelIdx][rowIdx];
                        symbol =
                          typeof symbolValue === "string"
                            ? parseInt(symbolValue, 10)
                            : symbolValue;
                      }
                    }
                  }

                  return {
                    id: lineId,
                    win: amount,
                    positions: transformedPositions,
                    symbol: symbol,
                  };
                })
                .filter((win) => win !== null);
            } else {
              transformedOutcome.wins = [];
            }

            return transformedOutcome;
          },
        },
      },
      propertyMap: {
        balance: "balance.wallet",
        bet: "outcome.bet",
        win: "outcome.win",
        token: "flow.round_id",
        round: "outcome",
        reels: "outcome.screen",
        wins: "outcome.wins",
      },
    };
  }

  /**
   * Initialize game session
   *
   * Reads configuration from window.__OPTIONS__ (server-embedded configuration),
   * makes POST request to init API endpoint, and returns normalized
   * game state matching standard format.
   *
   * @param {boolean} useApiData - If true, uses fresh API data instead of stored data (for error recovery)
   * @param {boolean} ignoreFreeRoundsStorage - If true, ignores free rounds localStorage (for new batch activation)
   * @returns {Promise<{bet: number, balance: number, token: string, currency: string, game: string, bets: number[], winningLines: number[][], paytable: Object}>}
   * @throws {Error} If window.__OPTIONS__ is missing or required fields are absent
   * @throws {Error} If API call fails (network error or API error response)
   */
  async initGame(useApiData = false, ignoreFreeRoundsStorage = false) {
    // Task 1: Extract window.__OPTIONS__ configuration
    if (!window.__OPTIONS__) {
      throw new Error(
        "PopiPlay: window.__OPTIONS__ not found. Game must be loaded from PopiPlay server.",
      );
    }

    const apiUrl = window.__OPTIONS__?.api;
    const playToken = window.__OPTIONS__?.play_token;
    const csrfHeaderName = window.__OPTIONS__?.csrfTokenHeaderName;
    const csrfHeaderValue = window.__OPTIONS__?.csrfTokenHeaderValue;
    const identifier = window.__OPTIONS__?.identifier;

    // Validate required fields
    const missingFields = [];
    if (!apiUrl) missingFields.push("api");
    if (!playToken) missingFields.push("play_token");
    if (!csrfHeaderName) missingFields.push("csrfTokenHeaderName");
    if (!csrfHeaderValue) missingFields.push("csrfTokenHeaderValue");
    if (!identifier) missingFields.push("identifier");

    if (missingFields.length > 0) {
      throw new Error(
        `PopiPlay: Missing required fields in window.__OPTIONS__: [${missingFields.join(
          ", ",
        )}]`,
      );
    }

    try {
      // CSRF header handling: Provider requires CSRF tokens for all API calls
      // CSRF header name and value are provided dynamically via window.__OPTIONS__
      // Header name is dynamic (e.g., "X-CSRF-Token") and value is a dynamic token
      // Both are included in the request headers using computed property name syntax
      const response = await axios.post(
        apiUrl, // API URL includes play_token in path (from window.__OPTIONS__.api)
        { command: "init" },
        {
          headers: {
            [csrfHeaderName]: csrfHeaderValue, // Dynamic CSRF header: header name and value from window.__OPTIONS__
            "Content-Type": "application/json",
          },
        },
      );

      // API version validation: Provider integration supports API version 2 only
      // This helps identify API version mismatches during development
      if (response.data?.api_version && response.data.api_version !== "2") {
        // API version validation - no logging in production
      }

      // Task 6: Catch invalid response structure
      if (!response.data) {
        throw new Error(
          "PopiPlay: Invalid API response - response.data is missing",
        );
      }

      // Task 6: Catch API error responses
      if (
        response.data.errors &&
        Array.isArray(response.data.errors) &&
        response.data.errors.length > 0
      ) {
        const error = response.data.errors[0];
        throw new Error(
          `PopiPlay: API error: ${error.desc} (code: ${error.code})`,
        );
      }

      // Task 3: Parse init response and extract required fields
      const availableBets = response.data?.options?.available_bets;
      const defaultBet = response.data?.options?.default_bet;
      const baseBet = response.data?.options?.base_bet;
      const paytable = response.data?.options?.paytable;
      const lines = response.data?.options?.lines;
      const wallet = response.data?.balance?.wallet;
      const currencyObj = response.data?.options?.currency;
      const featureMultipliers =
        response.data?.options?.feature_options?.feature_multipliers;

      // Validate response structure before accessing nested properties
      if (!availableBets || !Array.isArray(availableBets)) {
        throw new Error(
          "PopiPlay: Invalid API response - options.available_bets is missing or invalid",
        );
      }
      if (typeof defaultBet !== "number") {
        throw new Error(
          "PopiPlay: Invalid API response - options.default_bet is missing or invalid",
        );
      }
      if (typeof baseBet !== "number") {
        throw new Error(
          "PopiPlay: Invalid API response - options.base_bet is missing or invalid",
        );
      }
      if (!paytable || typeof paytable !== "object") {
        throw new Error(
          "PopiPlay: Invalid API response - options.paytable is missing or invalid",
        );
      }
      if (!lines || !Array.isArray(lines)) {
        throw new Error(
          "PopiPlay: Invalid API response - options.lines is missing or invalid",
        );
      }
      if (typeof wallet !== "number") {
        throw new Error(
          "PopiPlay: Invalid API response - balance.wallet is missing or invalid",
        );
      }
      if (!currencyObj || typeof currencyObj.code !== "string") {
        throw new Error(
          "PopiPlay: Invalid API response - options.currency.code is missing or invalid",
        );
      }

      // Calculate bonus buy multiplier if feature_multipliers present
      const bonusBuyMultiplier =
        featureMultipliers?.base_bet && featureMultipliers?.bonus_buy
          ? featureMultipliers.bonus_buy / featureMultipliers.base_bet
          : undefined;

      // Extract currency configuration for dynamic formatting
      // Currency object contains: { code, symbol, subunits, exponent }
      // - subunits: number of subunits per main unit (e.g., 100 for cents/dollars)
      // - exponent: number of decimal places to display (e.g., 2 for "10.00")
      // - symbol: currency symbol for display (e.g., "$", "€", "FUN")
      const currencyConfig = {
        subunits: currencyObj.subunits || 100, // Default to 100 if not provided
        exponent: currencyObj.exponent !== undefined ? currencyObj.exponent : 2, // Default to 2 if not provided
        symbol: currencyObj.symbol || currencyObj.code || "", // Fallback to code if symbol not provided
      };

      // ============================================
      // UNIVERSAL STATE CONSTRUCTION
      // ============================================

      // 1. Session validation
      this.validateSession();

      // 2. Detect feature state from API response
      let featureState = this.detectFeatureState(response.data);

      // 3. Load from storage (handles completion reloads and bet restoration)
      // Skip free rounds storage if ignoreFreeRoundsStorage is true (new batch activation)
      const storedState = this.loadGameState(response.data, ignoreFreeRoundsStorage);

      // 3a. If Free Rounds completion reload, rebuild featureState from localStorage
      // API returns base game after completion, so localStorage is our source of truth
      if (
        storedState.reloadState?.needsReload &&
        storedState.reloadState.type === "freeRounds" &&
        storedState.reloadState.phase === "completion"
      ) {
        featureState = {
          type: "freeRounds",
          phase: "completion",
          data: {
            count: storedState.reloadState.data.count,
            performed: storedState.reloadState.data.performed,
            totalWin: storedState.reloadState.data.totalWin || 0,
            betValue: storedState.reloadState.data.betValue,
            betLevel: storedState.reloadState.data.betLevel,
          },
        };
      }
      // 3b. If Free Rounds active reload, merge totalWin from storage into featureState
      else if (
        featureState.type === "freeRounds" &&
        storedState.reloadState?.needsReload &&
        storedState.reloadState.type === "freeRounds"
      ) {
        featureState.data.totalWin = storedState.reloadState.data.totalWin || 0;
      }

      // 4. If Hold & Win intro/active, add transformed reels to featureState
      // CRITICAL: Check featureState.type regardless of reloadState.type
      // When H&W triggers during Free Rounds, reloadState.type is 'freeRounds', not 'holdAndWin'
      if (
        featureState.type === "holdAndWin" &&
        (featureState.phase === "intro" || featureState.phase === "active")
      ) {
        const outcome = response.data?.outcome;
        if (outcome?.screen && Array.isArray(outcome.screen)) {
          const normalizedScreen = normalizeScreenData(outcome.screen);

          if (featureState.phase === "intro") {
            // Intro: Transform reels (coins + blanks)
            const transformedReels = transformForHoldAndWin(normalizedScreen);

            featureState.data.transformedReels = transformedReels;
            featureState.data.currentRound = {
              reels: transformedReels,
              wins: this.normalizeWins(outcome.wins, outcome.screen),
              win: outcome.win || 0,
              bet: outcome.bet || storedState.bet || defaultBet,
              storage: outcome.storage || null,
            };
          } else if (featureState.phase === "active") {
            // Active: Use screen as-is (already has coins + blanks)
            featureState.data.currentRound = {
              reels: normalizedScreen,
              wins: this.normalizeWins(outcome.wins, outcome.screen),
              win: outcome.win || 0,
              bet: outcome.bet || storedState.bet || defaultBet,
              storage: outcome.storage || null,
            };
          }
        }
      }

      // 5. Merge API + Storage
      // API balance is source of truth, but bet/round can come from storage
      // CRITICAL: Bet is user's choice - always preserve from storage, never reset to defaultBet
      // For error recovery (useApiData=true), use fresh API data for round only
      const balance = wallet;
      const savedBaseGame = StorageManager.loadBaseGameRound();
      const bet = storedState.bet || savedBaseGame?.bet || defaultBet;
      const round = useApiData ? this.extractRoundFromInit(response.data) : (storedState.round || this.extractRoundFromInit(response.data));

      if (useApiData) {
        // Using fresh API data for error recovery
      }

      // 6. Determine game mode
      const gameMode = this.getGameMode(featureState);

      // 7. Build universal result
      // Extract Free Rounds data if available (for when H&W is on top of Free Rounds)
      // Priority 1: Use freeRoundsData from loadGameState (when both features detected from API)
      // Priority 2: Check localStorage directly (when H&W takes priority in reloadState)
      // Priority 3: Extract from API response (when localStorage is empty)
      let freeRoundsData = storedState.freeRoundsData || null;
      
      if (!freeRoundsData) {
        const savedFreeRound = FEATURES_CONFIG.FREE_ROUNDS.ENABLED && !ignoreFreeRoundsStorage
          ? StorageManager.loadFreeRound()
          : null;
        
        if (savedFreeRound) {
          // CRITICAL: Free Rounds are "active" until user closes the outro popup
          // Even on last round (12/12), they should display as active with totalWin
          // The "played" status is only set after outro is dismissed
          // Use the MAXIMUM of API and localStorage (localStorage may have H&W win added)
          const apiTotalWin = response.data?.balance?.free_rounds ?? 0;
          const storageTotalWin = savedFreeRound.totalWin;
          const totalWin = Math.max(apiTotalWin, storageTotalWin);
          
          // Update localStorage if we're using a different value
          if (totalWin !== storageTotalWin) {
            StorageManager.saveFreeRound({
              round: savedFreeRound.round,
              bet: savedFreeRound.bet,
              balance: savedFreeRound.balance,
              performed: savedFreeRound.performed,
              count: savedFreeRound.count,
              totalWin: totalWin,
            });
          }
          
          freeRoundsData = {
            status: "active",
            count: savedFreeRound.count,
            performed: savedFreeRound.performed,
            betValue: savedFreeRound.bet,
            betLevel: null,
            totalWin: totalWin,
          };
        } else if (response.data?.free_rounds?.status === "active") {
          // Also check API response for active free rounds (when localStorage is empty)
          const freeRoundsTotalWin = response.data?.balance?.free_rounds || 0;
          freeRoundsData = {
            status: "active",
            count: response.data.free_rounds.count,
            performed: response.data.free_rounds.performed,
            betValue: response.data.free_rounds.bet_value,
            betLevel: response.data.free_rounds.bet_level,
            totalWin: freeRoundsTotalWin,
          };
        }
      }
      
      // CRITICAL: If featureState is Free Rounds but totalWin is missing, merge it from freeRoundsData
      // This happens when H&W was on top of Free Rounds and we reload
      if (freeRoundsData && featureState.type === "freeRounds" && featureState.phase === "active" && !featureState.data.totalWin) {
        featureState.data.totalWin = freeRoundsData.totalWin;
      }

      const result = {
        // Core game data
        balance: balance,
        bet: bet,
        baseBet: baseBet,
        bets: availableBets,
        round: round,
        token: playToken,
        currency: currencyObj.code,
        currencyConfig: currencyConfig,
        paytable: paytable,
        winningLines: lines,
        bonusBuyMultiplier: bonusBuyMultiplier,
        game: identifier,

        // Universal game mode
        gameMode: gameMode,

        // Universal feature state
        featureState: featureState,

        // Universal reload state
        reloadState: storedState.reloadState,

        // Free Rounds data (for when H&W is on top of Free Rounds)
        freeRoundsData: freeRoundsData,

        // Provider-specific data (for backward compatibility during migration)
        availableActions: response.data?.flow?.available_actions || ["init", "spin"],
        flowState: response.data?.flow?.state || "ready",
        features: response.data?.features || null,
        freeRounds: response.data?.free_rounds || null,
        command: "init",
      };

      // Extract free rounds total win from balance (for UI display)
      if (response.data?.balance?.free_rounds !== undefined) {
        result.freeRoundsTotalWin = response.data.balance.free_rounds || 0;
      }

      // Persist init state when reload came from API (no prior localStorage)
      // So future refreshes load from storage
      if (storedState.reloadState?.fromApi) {
        if (
          storedState.reloadState.type === "holdAndWin" &&
          FEATURES_CONFIG.HOLD_AND_WIN.ENABLED
        ) {
          StorageManager.saveBaseGameRound({
            round: result.round,
            bet: result.bet,
            balance: result.balance,
          });
          if (featureState.data?.currentRound) {
            const progressBar =
              storedState.reloadState.data?.progressBar ??
              featureState.data.currentRound?.storage?.progress_bar ??
              0;
            const coinMultipliers =
              storedState.reloadState.data?.coinMultipliers ??
              featureState.data.currentRound?.storage?.coin_multipliers ??
              null;
            StorageManager.saveHoldAndWinRound({
              round: featureState.data.currentRound,
              respinsLeft: featureState.data.respinsLeft ?? 0,
              progressBar,
              coinMultipliers,
              bet: result.bet,
              balance: result.balance,
              introShown: false,
            });
          }
          
          // CRITICAL: If H&W triggered during free rounds, also persist free rounds state
          // This ensures free rounds counter and totalWin are preserved on reload
          if (response.data.free_rounds?.status === "active") {
            const freeRoundBet = response.data.free_rounds.bet_value ?? result.bet;
            const freeRoundsTotalWin = response.data.balance?.free_rounds ?? 0;
            StorageManager.saveFreeRound({
              round: result.round,
              bet: freeRoundBet,
              balance: result.balance,
              performed: response.data.free_rounds.performed ?? 0,
              count: response.data.free_rounds.count ?? 0,
              totalWin: freeRoundsTotalWin,
            });
          }
        }
        
        // Also persist free rounds if they exist independently (not covered by H&W branch)
        if (
          storedState.reloadState.type === "freeRounds" &&
          FEATURES_CONFIG.FREE_ROUNDS.ENABLED
        ) {
          const frData = storedState.reloadState.data;
          if (frData) {
            // Use betValue (free round bet from API), not bet (may be outcome bet)
            const freeRoundBet = frData.betValue ?? frData.bet;
            StorageManager.saveFreeRound({
              round: frData.round,
              bet: freeRoundBet,
              balance: frData.balance,
              performed: frData.performed ?? 0,
              count: frData.count ?? 0,
              totalWin: frData.totalWin ?? 0,
            });
            if (result.round) {
              StorageManager.saveBaseGameRound({
                round: result.round,
                bet: result.bet,
                balance: result.balance,
              });
            }
          }
        }
      }

      // Persist base game from init when no prior storage (first load or cleared)
      // Same as we do for features - use init as source, store for consistency
      // ALSO persist when useApiData=true (error recovery) to save fresh API data
      if (
        result.gameMode === "base" &&
        result.round &&
        (!StorageManager.hasBaseGameRound() || useApiData)
      ) {
        StorageManager.saveBaseGameRound({
          round: result.round,
          bet: result.bet,
          balance: result.balance,
        });
      }

      return result;
    } catch (error) {
      // Task 6: Implement comprehensive error handling
      if (
        error.response?.data?.errors &&
        Array.isArray(error.response.data.errors)
      ) {
        const apiError = error.response.data.errors[0];
        const errorMessage = `PopiPlay: API error: ${apiError.desc} (code: ${apiError.code})`;
        throw new Error(errorMessage);
      }

      if (error.message && error.message.startsWith("PopiPlay:")) {
        // Already a PopiPlay error, just rethrow
        throw error;
      }

      // Network or other axios errors
      const errorMessage = `PopiPlay: Init API call failed: ${error.message}`;
      throw new Error(errorMessage);
    }
  }

  /**
   * Execute spin via API using Web Worker
   *
   * Creates a Promise wrapper around spin worker, sends bet and token to worker,
   * transforms response and updates token for subsequent requests.
   * Uses lazy worker initialization - worker is created on first spin() call and reused.
   *
   * Token handling:
   * - First spin: Uses play_token from window.__OPTIONS__ (session token)
   * - Subsequent spins: Uses flow.round_id from previous spin response (round identifier)
   * - Token rotates with each spin (flow.round_id), unlike initGame() which uses play_token
   *
   * Command handling:
   * - Regular game: Uses "spin" command
   * - Feature game: Uses "respin" command (determined by available_actions from API)
   *
   * @param {number} bet - Bet amount in subunits
   * @param {number|string} [token] - Optional token (play_token for first spin, flow.round_id for subsequent spins). If not provided, uses this.currentToken or options.play_token
   * @param {string} command - Command to send to API ("spin" or "respin") - determined by availableActions
   * @param {string} [purchasedFeature] - Optional purchased feature (e.g., "bonus_buy") to include in options
   * @param {Object} [spinOptions] - Optional options object to send to API (overrides default {bet, purchased_feature} structure)
   * @returns {Promise<{type: string, balance: number, round: Object, token: number|string, bet: number, win: number, availableActions: string[], flowState: string}>}
   * @throws {Error} If window.__OPTIONS__ is missing or required fields are absent
   * @throws {Error} If worker error occurs
   * @throws {Error} If API error occurs
   * @throws {Error} If response transformation fails
   */
  spin(bet, token, command, purchasedFeature, spinOptions) {
    return new Promise((resolve, reject) => {
      if (!this.worker) {
        this.worker = createInlineWorker(spinWorkerCode);
      }

      const options = window.__OPTIONS__;
      if (!options) {
        reject(
          new Error(
            "PopiPlay: window.__OPTIONS__ not found. Game must be loaded from PopiPlay server.",
          ),
        );
        return;
      }

      const apiUrl = options.api;
      const csrfTokenHeaderName = options.csrfTokenHeaderName;
      const csrfTokenHeaderValue = options.csrfTokenHeaderValue;
      const identifier = options.identifier;
      const playToken = options.play_token;

      // Task 3: Validate required fields
      const missingFields = [];
      if (!apiUrl) missingFields.push("api");
      if (!csrfTokenHeaderName) missingFields.push("csrfTokenHeaderName");
      if (!csrfTokenHeaderValue) missingFields.push("csrfTokenHeaderValue");
      if (!identifier) missingFields.push("identifier");

      if (missingFields.length > 0) {
        reject(
          new Error(
            `PopiPlay: Missing required fields in window.__OPTIONS__: [${missingFields.join(
              ", ",
            )}]`,
          ),
        );
        return;
      }

      // Set up worker message handler
      // Worker sends response in format: { balance, round, token, bet } (after extracting nested values)
      this.worker.onmessage = (event) => {
        const data = event.data;

        // Check for error in worker response
        if (data.error) {
          // Extract error code
          let errorCode = null;
          if (
            typeof data.error === "object" &&
            data.error.errors &&
            Array.isArray(data.error.errors)
          ) {
            errorCode = data.error.errors[0]?.code;
          }

          /* ============================================================
             DISABLED: Fake Spin Logic (Emergency Outcome Generation)

             Previously, errors would generate a fake non-winning combination
             and land the reels on it. This has been replaced with instant
             freeze logic in ReelControlContext.js.

             All errors now freeze reels mid-spin instead of landing on
             fake patterns. Keeping this code commented for reference.
             ============================================================

          // Only generate emergency pattern for non-205 errors
          // Error 205 (free rounds) will freeze reels, not land them
          if (errorCode !== 205) {
            const emergencyPattern = generateNonWinningCombination();
            const emergencyOutcome = {
              screen: emergencyPattern,
              reels: emergencyPattern,
              win: 0,
              wins: {},
              bet: bet
            };

            if (typeof data.error === 'object') {
              data.error.emergencyOutcome = emergencyOutcome;
            }
          }

          ============================================================ */

          // Extract balance from error response (API returns balance even on errors)
          // Pass balance to Redux for consistency with server state
          let extractedBalance = null;
          if (typeof data.error === "object" && data.error.balance) {
            if (
              typeof data.error.balance === "object" &&
              data.error.balance.wallet !== undefined
            ) {
              extractedBalance = data.error.balance.wallet;
            } else if (typeof data.error.balance === "number") {
              extractedBalance = data.error.balance;
            }
          }

          // Include balance if extracted
          if (extractedBalance !== null) {
            if (typeof data.error === "object") {
              data.error.balance = extractedBalance;
            }
          }

          // Reject with error
          if (typeof data.error === "object" && data.error.errors) {
            reject(data.error);
          } else {
            const normalizedError = {
              errors: [
                {
                  code: errorCode || 999,
                  desc:
                    typeof data.error === "string"
                      ? data.error
                      : "Unknown error",
                },
              ],
            };
            if (extractedBalance !== null) {
              normalizedError.balance = extractedBalance;
            }
            reject(normalizedError);
          }
          return;
        }

        // Provider-specific transformation: Normalize screen symbols to numbers
        const transformedData = this.transformReelsSymbols(data);

        // Token rotation pattern: Update token for subsequent requests
        // First spin: Uses play_token from window.__OPTIONS__
        // Subsequent spins: Uses flow.round_id from previous spin response (round identifier)
        // Extract token from API response (flow.round_id)
        if (transformedData.flow && transformedData.flow.round_id) {
          this.currentToken = transformedData.flow.round_id;
        }

        // Extract balance.wallet if balance is an object (provider API format)
        let extractedBalance = transformedData.balance;
        if (
          transformedData.balance &&
          typeof transformedData.balance === "object" &&
          transformedData.balance.wallet !== undefined
        ) {
          extractedBalance = transformedData.balance.wallet;
        }

        // Extract balance.free_rounds for casino-granted free rounds total win
        // Note: balance.free_rounds contains accumulated wins from previous rounds
        // balance.game contains current round's win
        // Total win = free_rounds + game
        let freeRoundsTotalWin = null;
        if (
          transformedData.balance &&
          typeof transformedData.balance === "object" &&
          transformedData.balance.free_rounds !== undefined
        ) {
          const accumulatedWin = transformedData.balance.free_rounds || 0;
          const currentGameWin = transformedData.balance.game || 0;
          freeRoundsTotalWin = accumulatedWin + currentGameWin;
        }

        // Normalize response structure for Redux
        // Redux slice expects: { round, token, bet, balance, availableActions, flowState }
        // Provider API returns: { outcome, flow, balance, features }

        // Apply win transformation using getResponseHandler logic
        // This transforms provider's win format from tuples to objects
        let normalizedOutcome = transformedData.outcome;
        if (transformedData.outcome) {
          const handler = this.getResponseHandler();
          normalizedOutcome = handler.patterns.regular.extractRound(
            transformedData.outcome,
          );
        }

        // Add round_id from flow to normalizedOutcome for deduplication tracking
        if (normalizedOutcome && transformedData.flow?.round_id) {
          normalizedOutcome.id = transformedData.flow.round_id;
        }

        // Handle hold and win feature game rounds
        const isHoldAndWinTrigger =
          FEATURES_CONFIG.HOLD_AND_WIN.ENABLED &&
          command === "spin" &&
          transformedData.flow?.state === "respin";
        const isRespin = command === "respin";

        // ============================================
        // UNIVERSAL STATE CONSTRUCTION
        // ============================================

        // 1. Detect feature state
        let featureState = this.detectFeatureState(transformedData);

        // 1a. Update Free Rounds totalWin from balance.free_rounds
        if (featureState.type === "freeRounds" && freeRoundsTotalWin !== null) {
          featureState.data.totalWin = freeRoundsTotalWin;
        }

        // 1b. Enhance featureState with Hold & Win transformed reels if needed
        if (featureState.type === "holdAndWin" && (isHoldAndWinTrigger || isRespin)) {
          const originalScreen = data.outcome?.screen;
          if (originalScreen && Array.isArray(originalScreen)) {
            const normalizedReels = normalizeScreenData(originalScreen);
            
            if (isHoldAndWinTrigger) {
              // Trigger: Transform reels (coins + blanks)
              const transformedReels = transformForHoldAndWin(normalizedReels);
              featureState.data.transformedReels = transformedReels;
              featureState.data.currentRound = {
                reels: transformedReels,
                wins: normalizedOutcome.wins || [],
                win: normalizedOutcome.win || 0,
                bet: normalizedOutcome.bet,
                storage: transformedData.outcome?.storage || null,
              };
            } else if (isRespin) {
              // Active respin: Use original screen as-is
              featureState.data.currentRound = {
                reels: normalizedReels,
                wins: normalizedOutcome.wins || [],
                win: normalizedOutcome.win || 0,
                bet: normalizedOutcome.bet,
                storage: transformedData.outcome?.storage || null,
              };
            }
          }
        }

        // 2. Determine game mode
        const gameMode = this.getGameMode(featureState);

        // 3. Calculate balance (handle frozen balance during free rounds)
        const winAmount = normalizedOutcome?.win || 0;
        let finalBalance;

        // Check if free rounds localStorage was cleared (warning popup closed)
        const savedFreeRound = StorageManager.loadFreeRound();
        const freeRoundsCleared = !savedFreeRound;

        if (featureState.type === "freeRounds" && featureState.phase === "active") {
          // Free rounds ACTIVE: balance stays frozen (wins accumulate in balance.free_rounds)
          finalBalance = extractedBalance;
        } else if (featureState.type === "freeRounds" && featureState.phase === "completion" && freeRoundsCleared) {
          // Free rounds COMPLETION with localStorage cleared: This is the first base game spin after warning closed
          // API still returns free_rounds.status="played" but doesn't deduct bet
          // We need to manually deduct the bet here
          const betAmount = normalizedOutcome?.bet || 0;
          finalBalance = extractedBalance - betAmount + winAmount;
        } else {
          // Regular gameplay: add win to balance immediately
          finalBalance = extractedBalance + winAmount;
        }

        // 4. Build game state for storage
        const gameState = {
          balance: finalBalance,
          bet: transformedData.outcome?.bet || null,
          round: normalizedOutcome, // Full round object with reels, wins, win
          gameMode: gameMode,
          featureState: featureState,
        };

        // 5. Save to storage
        this.saveGameState(gameState, transformedData);

        // 6. Clear completion data when starting new base game or free round spin (not H&W respin)
        if (command === "spin") {
          if (gameMode === "base") {
            this.clearCompletionData(); // Clear both H&W and free rounds
          } else if (featureState.type === "freeRounds") {
            this.clearHoldAndWinOnly(); // H&W finished during free rounds, clear it for next free round
          }
        }

        // 7. Build universal result
        const result = {
          // Core game data
          token: transformedData.flow?.round_id || null,
          bet: gameState.bet,
          balance: finalBalance,
          round: normalizedOutcome,

          // Universal game mode
          gameMode: gameMode,

          // Universal feature state
          featureState: featureState,

          // Provider-specific data (for backward compatibility)
          availableActions: transformedData.flow?.available_actions || ["init", "spin"],
          flowState: transformedData.flow?.state || "ready",
          features: transformedData.features || null,
          command: command,
          freeRounds: transformedData.free_rounds || null,
          freeRoundsTotalWin: freeRoundsTotalWin,
        };

        // CRITICAL: For respin commands, do NOT update popiGameSettingsSlice.js
        // Only update featureGameSettingsSlice.js with holdAndWinRound
        if (isRespin) {
          // Respin: Only update feature game state, NOT main game state
          const originalScreen = data.outcome?.screen; // Use raw data, not transformed
          if (originalScreen && Array.isArray(originalScreen)) {
            const respinRound = { ...normalizedOutcome };
            // Use original screen data as-is, just normalize string to numbers
            respinRound.reels = originalScreen.map((reel) => {
              if (!Array.isArray(reel)) return reel;
              return reel.map((symbolId) => {
                // Normalize string to number if needed
                return typeof symbolId === "string"
                  ? parseInt(symbolId, 10)
                  : symbolId;
              });
            });
            result.holdAndWinRound = respinRound;
          }
          // Do NOT set result.round - this prevents updating popiGameSettingsSlice.js
        } else if (isHoldAndWinTrigger) {
          // Trigger round: Update main game state with ORIGINAL reels (with buffers)
          result.round = normalizedOutcome; // Original reels with buffers for main game

          // CRITICAL: Backend sends screen with coins + regular symbols on trigger
          // We need to transform it to coins + blanks (0) for Hold and Win rendering
          const originalScreen = data.outcome?.screen; // Use raw data, not transformed
          if (originalScreen && Array.isArray(originalScreen)) {
            const normalizedReels = normalizeScreenData(originalScreen);
            const transformedReels = transformForHoldAndWin(normalizedReels);

            // Send transformed reels for Hold and Win rendering
            result.holdAndWinInitialReels = transformedReels;

            // Also send as current round so controllers initialize with correct symbolId
            const transformedRound = { ...normalizedOutcome };
            transformedRound.reels = transformedReels;
            result.holdAndWinRound = transformedRound;
          }
        } else {
          // Regular spin: Update main game state normally
          result.round = normalizedOutcome; // Use transformed outcome with normalized wins
        }

        // Return transformed data (with normalized reels and flow info)
        // Redux slice will call processRGSResponse() to normalize the response
        resolve(result);
      };

      // Task 9: Set up worker error handler
      this.worker.onerror = (event) => {
        const errorMessage = `PopiPlay: Worker error: ${
          event.message || "Unknown worker error"
        }`;
        reject(new Error(errorMessage));
      };

      // Send message to worker with required data
      // Token fallback logic:
      //   1. Use explicit token parameter if provided
      //   2. Use this.currentToken (flow.round_id from previous spin) if available
      //   3. Fall back to play_token from window.__OPTIONS__ (for first spin)
      // API URL structure: apiUrl always contains play_token in path (same URL used for all spins)
      // Token parameter changes: play_token for first spin, flow.round_id for subsequent spins
      // Command parameter: "spin" for regular game, "respin" for feature game

      const tokenToUse = token || this.currentToken || playToken;

      this.worker.postMessage({
        bet,
        token: tokenToUse, // Token rotation: play_token → flow.round_id
        apiUrl, // Full URL including play_token in path - same URL used for all spins
        csrfTokenName: csrfTokenHeaderName, // CSRF header name (dynamic, from window.__OPTIONS__)
        csrfTokenValue: csrfTokenHeaderValue, // CSRF header value (dynamic, from window.__OPTIONS__)
        identifier, // For reference, not used in API call
        command: command,
        ...(purchasedFeature ? { purchasedFeature } : {}),
        ...(spinOptions ? { options: spinOptions } : {}),
      });
    });
  }

  /**
   * Get spin parameters based on current game state
   * Determines command and spinOptions for API call
   * 
   * @param {Object} params - Universal game state
   * @param {number} params.bet - Current bet amount
   * @param {Object} params.featureState - Universal feature state
   * @param {string} params.purchasedFeature - Optional purchased feature
   * @returns {Object} { command, spinOptions } - Provider-specific API parameters
   */
  getSpinParams({ bet, featureState, purchasedFeature }) {
    // Free Rounds: PopiPlay sends "spin" with bonus options
    if (featureState?.type === "freeRounds") {
      return {
        command: "spin",
        spinOptions: { bonus: true, bet: featureState.data.betValue },
      };
    }
    
    // Hold & Win: PopiPlay sends "respin"
    if (featureState?.type === "holdAndWin") {
      return { command: "respin", spinOptions: undefined };
    }
    
    // Base game
    return { command: "spin", spinOptions: undefined };
  }

  /**
   * Simulate spin with predetermined outcome
   *
   * @param {array} slotface - Predetermined symbols
   * @param {number} bet - Bet amount in subunits
   * @param {string} token - Session token (play_token)
   * @returns {Promise<{token, round, bet, balance}>}
   * @throws {Error} "simulate() must be implemented"
   */
  async simulate(slotface, bet, token) {
    throw new Error("simulate() must be implemented");
  }

  // ============================================
  // UNIVERSAL STORAGE API
  // ============================================

  /**
   * Validate session (provider-specific)
   * PopiPlay uses cache_player_id from window.__OPTIONS__
   * @returns {boolean} True if session is valid, false if session changed
   */
  validateSession() {
    const currentSession = window.__OPTIONS__?.cache_player_id;
    const storedSession = StorageManager.getStoredSessionToken();

    if (!currentSession) {
      return false;
    }

    if (currentSession !== storedSession) {
      StorageManager.clearSessionData();
      StorageManager.saveSessionToken(currentSession);
      return false;
    }

    return true;
  }

  /**
   * Save game state to localStorage
   * Determines what to save based on game mode and feature state
   * @param {Object} gameState - Universal game state
   * @param {Object} apiResponse - Original API response (for feature-specific data)
   */
  saveGameState(gameState, apiResponse) {
    const { gameMode, featureState, round, bet, balance } = gameState;
    const { free_rounds } = apiResponse;

    // Save based on game mode and feature state
    if (gameMode === "base") {
      // Base game: save current round with full data
      // This serves as H&W return point if H&W triggers from base game
      StorageManager.saveBaseGameRound({ round, bet, balance });
    } else if (featureState.type === "freeRounds") {
      // Free Rounds: save to unified free_round key (both active and completion)
      // Use free round bet from feature (same pattern as base game stores its bet)
      const freeRoundBet = featureState.data.betValue ?? bet;
      
      // CRITICAL: If performed === 0 (new session) but we have existing free rounds in localStorage,
      // this means new free rounds were awarded during H&W outro of previous session.
      // DO NOT save them yet - let the completion flow finish first.
      const existingFreeRounds = StorageManager.loadFreeRound();
      const shouldSkipSave = featureState.data.performed === 0 && existingFreeRounds;
      
      if (shouldSkipSave) {
        // Don't save free rounds - completion handler will clear localStorage, then next spin will save new session
        // But still save base_game_round below
      } else {
        // CRITICAL: On first free round (performed === 0), save base game bet for restoration
        // This preserves the user's bet choice before free rounds started
        if (featureState.data.performed === 0) {
          const savedBaseGame = StorageManager.loadBaseGameRound();
          const baseGameBetToPreserve = savedBaseGame?.bet ?? bet;
          
          StorageManager.saveFreeRound({
            round,
            bet: freeRoundBet,
            balance,
            performed: featureState.data.performed,
            count: featureState.data.count,
            totalWin: featureState.data.totalWin || 0,
            betToRestore: baseGameBetToPreserve,
          });
        } else {
          // Subsequent free rounds: preserve existing betToRestore
          StorageManager.saveFreeRound({
            round,
            bet: freeRoundBet,
            balance,
            performed: featureState.data.performed,
            count: featureState.data.count,
            totalWin: featureState.data.totalWin || 0,
          });
        }
      }
      
      // ALSO save to base_game_round as H&W return point (only during active phase, not completion)
      // CRITICAL: Preserve existing base game bet, only update round and balance
      // CRITICAL: Do NOT save on completion - that would overwrite base game with H&W outro round (coins/blanks)
      if (featureState.phase === "active") {
        const savedBaseGame = StorageManager.loadBaseGameRound();
        const baseGameBetToKeep = savedBaseGame?.bet ?? bet;
        StorageManager.saveBaseGameRound({ round, bet: baseGameBetToKeep, balance });
      }
    } else if (featureState.type === "holdAndWin") {
      if (featureState.phase === "intro") {
        // Trigger round (natural or bonus buy): save base game for return point after H&W
        StorageManager.saveBaseGameRound({ round, bet, balance });
        
        // CRITICAL: Save H&W intro state for reload support
        // This allows player to reload during intro and resume correctly
        StorageManager.saveHoldAndWinRound({
          round: featureState.data.currentRound,
          respinsLeft: featureState.data.respinsLeft,
          progressBar: featureState.data.progressBar || 0,
          coinMultipliers: featureState.data.coinMultipliers || null,
          bet,
          balance,
          introShown: false,
        });
        
        // CRITICAL: If H&W triggered during free rounds, also save free rounds state
        // This ensures free rounds counter and totalWin are preserved on reload
        // Check for BOTH "active" and "played" status (last free round triggers H&W, status becomes "played")
        if (free_rounds?.status === "active" || free_rounds?.status === "played") {
          const freeRoundBet = free_rounds.bet_value ?? bet;
          
          // CRITICAL: Preserve existing free round's round data (don't overwrite with H&W trigger)
          // The 'round' variable here is the H&W trigger round (transformed to coins/blanks)
          // We need to keep the original free round data that was saved before H&W triggered
          const existingFreeRound = StorageManager.loadFreeRound();
          const roundToSave = existingFreeRound?.round || round;
          
          // CRITICAL: API's balance.free_rounds is the OLD total (before trigger round).
          // Trigger round's win is in balance.game. Add them together for correct total.
          const accumulatedWin = apiResponse.balance?.free_rounds ?? 0;
          const triggerRoundWin = apiResponse.balance?.game ?? 0;
          const freeRoundsTotalWin = accumulatedWin + triggerRoundWin;
          
          StorageManager.saveFreeRound({
            round: roundToSave,
            bet: freeRoundBet,
            balance,
            performed: free_rounds.performed,
            count: free_rounds.count,
            totalWin: freeRoundsTotalWin,
          });
        }
      } else if (featureState.phase === "active") {
        // Check if this is the final round (respins === 0)
        const isFinalRound = featureState.data.respinsLeft === 0;

        // Save H&W round state (active or completion)
        const maxWinThreshold = bet * FEATURES_CONFIG.HOLD_AND_WIN.MAX_WIN_MULTIPLIER;
        const totalWin = featureState.data.currentRound?.win || 0;
        const isMaxWin = totalWin >= maxWinThreshold;

        StorageManager.saveHoldAndWinRound({
          round: featureState.data.currentRound,
          respinsLeft: featureState.data.respinsLeft,
          progressBar: featureState.data.progressBar || 0,
          coinMultipliers: featureState.data.coinMultipliers || null,
          bet,
          balance,
          introShown: true,
          // Add completion fields when respinsLeft === 0
          ...(isFinalRound && {
            totalWin,
            isMaxWin,
          }),
        });

        // CRITICAL: If H&W is running during free rounds, keep free rounds state updated
        // This ensures mid-feature reloads have the correct totalWin (trigger round win included)
        //
        // BUG FIX: Previously, on H&W completion during free rounds, we were saving the H&W outro
        // round (coins/blanks) to free_round storage, overwriting the actual free round data.
        // This caused refreshGameData() to load the H&W outro round instead of the free round.
        // Now we preserve the existing free round's round data and only update totalWin.
        if (free_rounds?.status === "active" || free_rounds?.status === "played") {
          const savedFreeRound = StorageManager.loadFreeRound();
          const freeRoundBet = free_rounds.bet_value ?? bet;
          
          if (isFinalRound) {
            // Completion: Use authoritative API value (balance.free_rounds + balance.game)
            // This is more reliable than existingTotalWin + hnwWin because the server
            // has now credited everything and balance reflects the true combined total.
            const accumulatedWin = apiResponse.balance?.free_rounds ?? 0;
            const currentGameWin = apiResponse.balance?.game ?? 0;
            const updatedTotalWin = accumulatedWin + currentGameWin;

            // CRITICAL: Preserve the free round's round data (don't overwrite with H&W outro round)
            // The 'round' variable here is the H&W outro round (coins/blanks), not the actual free round
            // We only want to update totalWin, not replace the round data
            const existingRound = savedFreeRound?.round || round;
            
            StorageManager.saveFreeRound({
              round: existingRound,
              bet: freeRoundBet,
              balance,
              performed: free_rounds.performed,
              count: free_rounds.count,
              totalWin: updatedTotalWin,
            });
          } else {
            // Mid-feature: Just keep free rounds state fresh (totalWin unchanged)
            // This ensures mid-feature reload has the correct totalWin from intro
            if (savedFreeRound) {
              // CRITICAL: Preserve existing round data during mid-feature updates
              const existingRound = savedFreeRound.round;
              
              StorageManager.saveFreeRound({
                round: existingRound,
                bet: freeRoundBet,
                balance,
                performed: free_rounds.performed,
                count: free_rounds.count,
                totalWin: savedFreeRound.totalWin,
              });
            }
          }
        }
      }
    }
  }

  /**
   * REFACTORED HELPER METHODS FOR loadGameState()
   * These methods break down the complex scenario detection into manageable pieces
   */

  /**
   * Collect all feature states from storage and API
   * @param {Object} apiResponse - API response data
   * @param {boolean} ignoreFreeRoundsStorage - If true, skips loading free rounds from localStorage
   * @returns {Object} Feature states organized by type
   */
  collectFeatureStates(apiResponse, ignoreFreeRoundsStorage = false) {
    return {
      holdAndWin: {
        storage: FEATURES_CONFIG.HOLD_AND_WIN.ENABLED
          ? StorageManager.loadHoldAndWinRound()
          : null,
        api: this.detectHoldAndWinFromApi(apiResponse)
      },
      freeRounds: {
        storage: FEATURES_CONFIG.FREE_ROUNDS.ENABLED && !ignoreFreeRoundsStorage
          ? StorageManager.loadFreeRound()
          : null,
        api: this.detectFreeRoundsFromApi(apiResponse)
      }
    };
  }

  /**
   * Determine feature priority (which feature is "on top")
   * Priority order: H&W > Free Rounds > Base
   * @param {Object} features - Feature states from collectFeatureStates()
   * @returns {Object} Priority object with primary and secondary features
   */
  determineFeaturePriority(features) {
    const hnw = features.holdAndWin;
    const fr = features.freeRounds;

    // Priority 1: Hold & Win (always on top when present)
    if (hnw.storage || hnw.api) {
      return {
        primary: 'holdAndWin',
        secondary: (fr.storage || fr.api) ? 'freeRounds' : null
      };
    }

    // Priority 2: Free Rounds
    if (fr.storage || fr.api) {
      return {
        primary: 'freeRounds',
        secondary: null
      };
    }

    // Priority 3: Base game
    return {
      primary: 'base',
      secondary: null
    };
  }

  /**
   * Build reload state for Hold & Win feature
   * Handles all H&W scenarios (with or without secondary Free Rounds)
   * @param {Object} features - Feature states
   * @param {Object} priority - Priority object
   * @param {Object} apiResponse - API response
   * @returns {Object} Reload state object
   */
  buildHoldAndWinReload(features, priority, apiResponse) {
    const hnw = features.holdAndWin;
    const fr = features.freeRounds;
    const { outcome, options, free_rounds } = apiResponse;

    // Determine H&W phase
    let hnwPhase;
    let hnwData;

    if (hnw.storage) {
      // Load from storage
      hnwData = hnw.storage;
      if (hnwData.respinsLeft === 0) {
        hnwPhase = "completion";
      } else if (hnwData.introShown === false) {
        hnwPhase = "intro";
      } else {
        hnwPhase = "active";
      }
    } else {
      // Load from API
      hnwPhase = hnw.api.phase;
      hnwData = hnw.api.data;
    }

    // Get trigger round (return point after H&W)
    const savedBaseGame = StorageManager.loadBaseGameRound();
    const triggerRound = savedBaseGame?.round || this.extractTriggerRoundFromInit(apiResponse);

    // Determine bet
    const bet = hnw.storage ? hnw.storage.bet : (outcome?.bet ?? options?.default_bet ?? null);

    // Build base result
    // Set fromApi flag when FR comes from API (even if H&W is from storage)
    const hasApiData = hnw.api || (priority.secondary === 'freeRounds' && fr.api);
    
    const result = {
      reloadState: {
        needsReload: true,
        type: "holdAndWin",
        phase: hnwPhase,
        data: hnwData,
        ...(hasApiData && { fromApi: true })
      },
      bet,
      round: triggerRound
    };

    // Add secondary Free Rounds data if present
    if (priority.secondary === 'freeRounds') {
      const freeRoundBet = fr.storage
        ? fr.storage.bet
        : (free_rounds?.bet_value ?? outcome?.bet ?? options?.default_bet ?? null);

      const freeRoundsTotalWin = fr.storage
        ? fr.storage.totalWin
        : (apiResponse?.balance?.free_rounds ?? 0);

      result.freeRoundsData = {
        status: "active",
        count: fr.storage ? fr.storage.count : fr.api.count,
        performed: fr.storage ? fr.storage.performed : fr.api.performed,
        betValue: freeRoundBet,
        betLevel: fr.storage ? null : (fr.api?.betLevel ?? null),
        totalWin: freeRoundsTotalWin
      };
    }

    return result;
  }

  /**
   * Build reload state for Free Rounds feature
   * @param {Object} features - Feature states
   * @param {Object} apiResponse - API response
   * @returns {Object} Reload state object
   */
  buildFreeRoundsReload(features, apiResponse) {
    const fr = features.freeRounds;
    const { outcome, options, free_rounds } = apiResponse;

    // Determine phase
    let phase;
    let frData;

    if (fr.storage) {
      // Load from storage
      phase = fr.storage.performed === fr.storage.count ? "completion" : "active";
      frData = fr.storage;
    } else {
      // Load from API
      phase = fr.api.phase;
      
      const savedBaseGame = StorageManager.loadBaseGameRound();
      const freeRoundBet = free_rounds?.bet_value ?? outcome?.bet ?? options?.default_bet ?? null;
      const round = savedBaseGame?.round ?? (outcome?.screen ? {
        reels: normalizeScreenData(outcome.screen),
        wins: outcome.wins || [],
        win: outcome.win || 0
      } : null);

      frData = {
        round,
        bet: freeRoundBet,
        balance: apiResponse?.balance?.wallet,
        performed: fr.api.performed,
        count: fr.api.count,
        totalWin: apiResponse?.balance?.free_rounds ?? 0,
        betValue: freeRoundBet,
        betLevel: fr.api.betLevel
      };
    }

    // Determine bet (restore base game bet on completion)
    let betToUse;
    if (phase === "completion") {
      const savedBaseGame = StorageManager.loadBaseGameRound();
      const betToRestore = fr.storage?.betToRestore ?? savedBaseGame?.bet;
      betToUse = betToRestore || frData.bet;
    } else {
      betToUse = frData.bet;
    }

    return {
      round: frData.round,
      bet: betToUse,
      reloadState: {
        needsReload: true,
        type: "freeRounds",
        phase: phase,
        ...(fr.api && { fromApi: true }),
        data: {
          round: frData.round,
          bet: frData.bet,
          balance: frData.balance,
          performed: frData.performed,
          count: frData.count,
          totalWin: frData.totalWin ?? 0,
          betValue: frData.betValue ?? frData.bet,
          betLevel: frData.betLevel ?? null
        }
      }
    };
  }

  /**
   * Build reload state for base game (no features)
   * @returns {Object} Reload state object
   */
  buildBaseGameReload() {
    const savedBaseGame = StorageManager.loadBaseGameRound();
    
    if (savedBaseGame) {
      return {
        round: savedBaseGame.round,
        bet: savedBaseGame.bet,
        reloadState: { needsReload: false }
      };
    }

    return { reloadState: { needsReload: false } };
  }

  /**
   * Load game state from localStorage (REFACTORED)
   * Returns merged state prioritizing completion reloads
   * @param {Object} apiResponse - API response data
   * @param {boolean} ignoreFreeRoundsStorage - If true, skips loading free rounds from localStorage
   * @returns {Object} Stored state with reloadState, bet, round
   */
  loadGameState(apiResponse, ignoreFreeRoundsStorage = false) {
    // REFACTORED: Use helper methods to determine priority and build state
    // This makes the logic easier to understand and extend
    
    // Step 1: Collect all feature states from storage and API
    const features = this.collectFeatureStates(apiResponse, ignoreFreeRoundsStorage);
    
    // Step 2: Determine which feature is primary (on top)
    const priority = this.determineFeaturePriority(features);
    
    // Step 3: Build reload state based on priority
    if (priority.primary === 'holdAndWin') {
      return this.buildHoldAndWinReload(features, priority, apiResponse);
    }
    
    if (priority.primary === 'freeRounds') {
      // Special case: Check if FR completion in API but storage cleared (warning already closed)
      if (features.freeRounds.api && features.freeRounds.api.phase === "completion" && !features.freeRounds.storage) {
        // Treat as base game
        return this.buildBaseGameReload();
      }
      return this.buildFreeRoundsReload(features, apiResponse);
    }
    
    // Base game
    return this.buildBaseGameReload();
  }

  /**
   * Clear completion data from localStorage
   * Called when starting a new base game spin (clears both H&W and free rounds)
   */
  clearCompletionData() {
    if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED) {
      if (StorageManager.hasHoldAndWinRound()) {
        StorageManager.clearHoldAndWinRound();
      }
    }
    if (FEATURES_CONFIG.FREE_ROUNDS.ENABLED) {
      if (StorageManager.hasFreeRound()) {
        StorageManager.clearFreeRound();
      }
    }
  }

  /**
   * Clear only Hold and Win data (keep free rounds)
   * Called when starting a new free round spin after H&W completed during free rounds
   */
  clearHoldAndWinOnly() {
    if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED) {
      if (StorageManager.hasHoldAndWinRound()) {
        StorageManager.clearHoldAndWinRound();
      }
    }
  }

  /**
   * Helper: Detect feature state from raw API response
   * Used internally by loadGameState before full transformation
   * @param {Object} apiResponse - Raw API response data
   * @returns {Object} Feature state object
   */
  detectFeatureStateFromRawResponse(apiResponse) {
    const { flow, features, free_rounds } = apiResponse;

    // Check Hold & Win (flow.state or trigger_screen for init responses)
    if (
      FEATURES_CONFIG.HOLD_AND_WIN.ENABLED &&
      (flow?.state === "respin" || features?.trigger_screen !== undefined) &&
      features?.respins_left !== undefined
    ) {
      const phase =
        features.respins_left === 0
          ? "completion"
          : hasRegularSymbols(apiResponse.outcome?.screen)
            ? "intro"
            : "active";

      return {
        type: "holdAndWin",
        phase: phase,
        data: {
          respinsLeft: features.respins_left,
        },
      };
    }

    // Check Free Rounds
    if (FEATURES_CONFIG.FREE_ROUNDS.ENABLED && free_rounds?.status) {
      if (free_rounds.status === "active") {
        return {
          type: "freeRounds",
          phase: "active",
          data: {
            count: free_rounds.count,
            performed: free_rounds.performed,
          },
        };
      }
      if (free_rounds.status === "played") {
        return {
          type: "freeRounds",
          phase: "completion",
          data: {},
        };
      }
    }

    return { type: null, phase: null, data: null };
  }

  // ============================================
  // UNIVERSAL FEATURE DETECTION
  // ============================================

  /**
   * Detect feature state from API response
   * Translates PopiPlay-specific signals to universal format
   * @param {Object} apiResponse - Transformed API response data
   * @returns {Object} Universal feature state
   */
  detectFeatureState(apiResponse) {
    const { flow, features, free_rounds, outcome } = apiResponse;

    // PopiPlay-specific: flow.state === "respin" OR features.respins_left exists signals Hold & Win
    // CRITICAL: When respins_left === 0 (final round), flow.state may change from "respin" to "closed"
    // We must still detect this as Hold & Win to allow collection to trigger
    if (
      FEATURES_CONFIG.HOLD_AND_WIN.ENABLED &&
      features?.respins_left !== undefined &&
      (flow?.state === "respin" || features.trigger_screen !== undefined)
    ) {
      const phase = this.detectHoldAndWinPhase(features, outcome);

      return {
        type: "holdAndWin",
        phase: phase,
        data: {
          respinsLeft: features.respins_left,
          triggerScreen: features.trigger_screen,
          progressBar: outcome?.storage?.progress_bar || 0,
          coinMultipliers: outcome?.storage?.coin_multipliers || null,
        },
      };
    }

    // PopiPlay-specific: free_rounds.status === "active" signals Free Rounds
    if (
      FEATURES_CONFIG.FREE_ROUNDS.ENABLED &&
      free_rounds?.status === "active"
    ) {
      return {
        type: "freeRounds",
        phase: "active",
        data: {
          count: free_rounds.count,
          performed: free_rounds.performed,
          betValue: free_rounds.bet_value,
          betLevel: free_rounds.bet_level,
          totalWin: 0, // Will be updated from balance.free_rounds
        },
      };
    }

    // PopiPlay-specific: free_rounds.status === "played" signals completion
    if (
      FEATURES_CONFIG.FREE_ROUNDS.ENABLED &&
      free_rounds?.status === "played"
    ) {
      // Extract totalWin from balance.free_rounds (API's source of truth for completion)
      const freeRoundsTotalWin = apiResponse?.balance?.free_rounds ?? 0;
      
      return {
        type: "freeRounds",
        phase: "completion",
        data: {
          count: free_rounds.count,
          performed: free_rounds.performed,
          totalWin: freeRoundsTotalWin,
        },
      };
    }

    return { type: null, phase: null, data: null };
  }

  /**
   * Detect Hold & Win from API response
   * @param {Object} apiResponse - API response
   * @returns {Object|null} H&W state or null
   */
  detectHoldAndWinFromApi(apiResponse) {
    const { flow, features, outcome } = apiResponse;
    
    if (
      !FEATURES_CONFIG.HOLD_AND_WIN.ENABLED ||
      features?.respins_left === undefined ||
      (flow?.state !== "respin" && features?.trigger_screen === undefined)
    ) {
      return null;
    }

    const phase = this.detectHoldAndWinPhase(features, outcome);

    return {
      phase,
      data: {
        respinsLeft: features.respins_left,
        triggerScreen: features.trigger_screen,
        progressBar: outcome?.storage?.progress_bar || 0,
        coinMultipliers: outcome?.storage?.coin_multipliers || null,
      },
    };
  }

  /**
   * Detect Free Rounds from API response
   * @param {Object} apiResponse - API response
   * @returns {Object|null} Free Rounds state or null
   */
  detectFreeRoundsFromApi(apiResponse) {
    const { free_rounds } = apiResponse;
    
    if (!FEATURES_CONFIG.FREE_ROUNDS.ENABLED || !free_rounds?.status) {
      return null;
    }

    if (free_rounds.status === "active") {
      return {
        phase: "active",
        count: free_rounds.count,
        performed: free_rounds.performed,
        betValue: free_rounds.bet_value,
        betLevel: free_rounds.bet_level,
      };
    }

    if (free_rounds.status === "played") {
      return {
        phase: "completion",
        count: free_rounds.count,
        performed: free_rounds.performed,
      };
    }

    return null;
  }

  /**
   * Detect Hold & Win phase
   * PopiPlay-specific: uses respins_left and screen analysis
   * 
   * IMPORTANT: During normal gameplay, this only returns "intro" or "active"
   * The "completion" phase is ONLY used for reload scenarios (from localStorage)
   * When respins_left === 0 during gameplay, we return "active" to allow collection to trigger
   * 
   * @param {Object} features - Features object from API
   * @param {Object} outcome - Outcome object from API
   * @returns {string} Phase: "intro" | "active" (never "completion" during gameplay)
   */
  detectHoldAndWinPhase(features, outcome) {
    // Intro: Has regular symbols (not just coins/blanks)
    if (hasRegularSymbols(outcome?.screen)) {
      return "intro";
    }

    // Active: Mid-feature (only coins and blanks)
    // This includes respins_left === 0 (final round before collection)
    return "active";
  }

  /**
   * Determine game mode from feature state
   * @param {Object} featureState - Feature state object
   * @returns {string} Game mode: "base" | "feature"
   */
  getGameMode(featureState) {
    // H&W trigger rounds (intro phase) are base game spins that trigger the feature
    // They need bet deduction just like regular base game spins
    // Free rounds trigger rounds (intro phase) are also base game spins
    if (featureState.type && featureState.phase === "intro") {
      return "base";
    }
    
    // Active feature gameplay (respins, free rounds active) is "feature" mode
    return featureState.type ? "feature" : "base";
  }

  /**
   * Extract free rounds total win from API response
   * @param {Object} apiResponse - API response data
   * @returns {number} Total win amount
   */
  extractFreeRoundsTotalWin(apiResponse) {
    // PopiPlay sends total win in balance.free_rounds + balance.game
    const accumulatedWin = apiResponse.balance?.free_rounds || 0;
    const currentGameWin = apiResponse.balance?.game || 0;
    return accumulatedWin + currentGameWin;
  }

  /**
   * Extract round data from initGame API response
   * @param {Object} apiResponse - API response data
   * @returns {Object|null} Round object or null
   */
  extractRoundFromInit(apiResponse) {
    // For normal init, use options.screen if available
    const screen = apiResponse?.options?.screen;
    if (screen && Array.isArray(screen)) {
      const normalizedScreen = normalizeScreenData(screen);
      return {
        reels: normalizedScreen,
        wins: [],
        win: 0,
      };
    }
    return null;
  }

  /**
   * Extract trigger round for Hold and Win reload
   * Prefers features.trigger_screen (actual coins that triggered H&W) over options.screen (random base layout)
   * @param {Object} apiResponse - API response data
   * @returns {Object|null} Round object or null
   */
  /**
   * Normalizes raw wins array from API (tuple format) into the object format
   * expected by matchHorizontalWinningLines: { id, win, positions, symbol }.
   * Same logic as the extractRound transform used during regular spins.
   */
  normalizeWins(rawWins, screen) {
    if (!Array.isArray(rawWins)) return [];
    return rawWins
      .map((winTuple) => {
        if (!Array.isArray(winTuple) || winTuple.length < 4) return null;
        const [, amount, positions, lineId] = winTuple;
        if (!Array.isArray(positions)) return null;
        const transformedPositions = positions.map((rowValue, reelIndex) => {
          if (Array.isArray(rowValue) && rowValue.length >= 2) {
            return [rowValue[0], rowValue[1]];
          }
          return [reelIndex, rowValue];
        });
        let symbol = null;
        if (Array.isArray(screen) && transformedPositions.length > 0) {
          const [reelIdx, rowIdx] = transformedPositions[0];
          if (screen[reelIdx]?.[rowIdx] !== undefined) {
            const sv = screen[reelIdx][rowIdx];
            symbol = typeof sv === "string" ? parseInt(sv, 10) : sv;
          }
        }
        return { id: lineId, win: amount, positions: transformedPositions, symbol };
      })
      .filter((w) => w !== null);
  }

  extractTriggerRoundFromInit(apiResponse) {
    const triggerScreen = apiResponse?.features?.trigger_screen;
    if (triggerScreen && Array.isArray(triggerScreen)) {
      const normalizedScreen = normalizeScreenData(triggerScreen);
      return {
        reels: normalizedScreen,
        wins: this.normalizeWins(apiResponse?.outcome?.wins, apiResponse?.outcome?.screen),
        win: apiResponse?.outcome?.win || 0,
      };
    }
    return this.extractRoundFromInit(apiResponse);
  }

  /**
   * Extract round ID from API response
   * @param {Object} apiResponse - API response data
   * @returns {string|number} Round ID
   */
  extractRound(apiResponse) {
    return apiResponse?.flow?.round_id || apiResponse?.outcome?.round_id || null;
  }

  /**
   * Get balance management strategy for this provider
   * 
   * PopiPlay balance model:
   * - Server returns OLD balance (does NOT include current win)
   * - Client should: deduct bet immediately, add win after animations, sync on next spin
   * - Client-side win addition is required
   * 
   * @returns {Object} Balance strategy configuration
   */
  getBalanceStrategy() {
    return {
      // Server balance does NOT include the current round's win
      serverIncludesWin: false,
      
      // Client should deduct bet immediately on successful response
      clientDeductsBet: true,
      
      // Client MUST add win after animations (server hasn't added it yet)
      clientAddsWin: true,
    };
  }

  /**
   * Determine if balance should be synced with server when win animation closes
   * 
   * PopiPlay: YES - Server tracks all transactions and provides accurate balance
   * 
   * @returns {boolean} True if balance should sync on win close
   */
  shouldSyncBalanceOnWinClose() {
    return true;
  }
}

// Export spinWorkerCode for testing purposes
export { spinWorkerCode };

export default PopiPlayRGS;
